import { edgeKey } from "./mesh-face-selection";
/** Vertices closer than this to the terrain are treated as touching it (m). */
const ABOVE_TERRAIN_TOLERANCE = 1e-3;
/**
 * Extend the open boundary of a TIN vertically down to the terrain.
 * Boundary edges above the terrain get a vertical skirt; vertices already below
 * the terrain, or outside the sampler's coverage, are left untouched.
 * Edges longer than interpolationPitch are subdivided and the adjacent triangle is
 * fan-split so the result stays manifold (no T-junctions on the outer ring).
 * Vertices sharing a position are welded first: the consumer merges them anyway, so
 * without welding a duplicated seam would read as an open boundary and grow a skirt
 * inside the mesh.
 * @param sampleHeight Terrain height lookup in the geometry's coordinate space.
 * @param interpolationPitch Max XY length of a skirt segment (m). Non-positive disables subdivision.
 * @returns New geometry; the input is not mutated.
 */
export function extendOpenBoundaryToTerrain(geometry, sampleHeight, interpolationPitch) {
    const welded = weldVertices(geometry);
    const vertices = [...welded.vertices];
    const indices = [...welded.indices];
    const boundaryEdges = collectBoundaryEdges(welded);
    // --- shared state across edges
    // Edge key -> face currently owning that edge; updated when a face is fan-split.
    const edgeOwner = new Map();
    for (const edge of boundaryEdges) {
        edgeOwner.set(edgeKey(edge.a, edge.b), edge.face);
    }
    // Top vertex index -> its dropped counterpart, so adjacent skirts share vertices.
    const bottomOf = new Map();
    const addVertex = (x, y, z) => {
        vertices.push(x, y, z);
        return vertices.length / 3 - 1;
    };
    const dropVertex = (top) => {
        if (bottomOf.has(top))
            return bottomOf.get(top);
        const x = vertices[top * 3];
        const y = vertices[top * 3 + 1];
        const z = vertices[top * 3 + 2];
        const h = sampleHeight(x, y);
        const bottom = h !== undefined && Number.isFinite(h) && z - h > ABOVE_TERRAIN_TOLERANCE
            ? addVertex(x, y, h)
            : undefined;
        bottomOf.set(top, bottom);
        return bottom;
    };
    // --- per boundary edge: subdivide, fan-split owner face, build skirt
    for (const edge of boundaryEdges) {
        const key = edgeKey(edge.a, edge.b);
        const owner = edgeOwner.get(key);
        if (owner === undefined)
            continue;
        const topChain = subdivideEdge(vertices, edge.a, edge.b, interpolationPitch, addVertex);
        if (topChain.length > 2) {
            fanSplitFace(indices, owner, edge.a, edge.b, topChain, edgeOwner);
        }
        for (let i = 0; i < topChain.length - 1; i++) {
            const ta = topChain[i];
            const tb = topChain[i + 1];
            const ba = dropVertex(ta);
            const bb = dropVertex(tb);
            // Winding (b, a, ...) mirrors the owner face's a -> b traversal so the
            // skirt normal points away from the mesh, consistent with the owner.
            if (ba !== undefined && bb !== undefined) {
                indices.push(tb, ta, ba, tb, ba, bb);
            }
            else if (ba !== undefined) {
                indices.push(tb, ta, ba);
            }
            else if (bb !== undefined) {
                indices.push(tb, ta, bb);
            }
        }
    }
    return { vertices, indices };
}
/** Vertices closer than this are the same point (m); imported seams differ by ~1e-11. */
export const DEFAULT_WELD_TOLERANCE = 1e-6;
/**
 * Merge vertices within `tolerance` of each other and drop faces that degenerate
 * as a result. Imported design data carries seams whose two sides differ by
 * floating-point noise (~1e-11 m); an exact match leaves them open, and
 * stitching then splits edges at their own endpoints and makes the mesh
 * non-manifold.
 * @param tolerance Max distance between two vertices that merge (m). Non-positive means exact match.
 */
export function weldVertices(geometry, tolerance = DEFAULT_WELD_TOLERANCE) {
    const vertices = [];
    const indices = [];
    // Cell key -> welded vertex indices in that cell; a lookup scans the 27
    // neighbouring cells so two points straddling a cell border still merge.
    const cells = new Map();
    const remapped = new Map();
    const cellSize = tolerance > 0 ? tolerance : 0;
    const cellOf = (value) => cellSize > 0 ? Math.floor(value / cellSize) : value;
    const findNearby = (x, y, z) => {
        const cx = cellOf(x);
        const cy = cellOf(y);
        const cz = cellOf(z);
        const reach = cellSize > 0 ? 1 : 0;
        for (let ox = -reach; ox <= reach; ox++) {
            for (let oy = -reach; oy <= reach; oy++) {
                for (let oz = -reach; oz <= reach; oz++) {
                    for (const candidate of cells.get(`${cx + ox} ${cy + oy} ${cz + oz}`) ?? []) {
                        const dx = vertices[candidate * 3] - x;
                        const dy = vertices[candidate * 3 + 1] - y;
                        const dz = vertices[candidate * 3 + 2] - z;
                        if (Math.hypot(dx, dy, dz) <= cellSize)
                            return candidate;
                    }
                }
            }
        }
        return undefined;
    };
    const weldedIndexOf = (original) => {
        const cached = remapped.get(original);
        if (cached !== undefined)
            return cached;
        const x = geometry.vertices[original * 3];
        const y = geometry.vertices[original * 3 + 1];
        const z = geometry.vertices[original * 3 + 2];
        let index = findNearby(x, y, z);
        if (index === undefined) {
            vertices.push(x, y, z);
            index = vertices.length / 3 - 1;
            const key = `${cellOf(x)} ${cellOf(y)} ${cellOf(z)}`;
            const cell = cells.get(key);
            if (cell)
                cell.push(index);
            else
                cells.set(key, [index]);
        }
        remapped.set(original, index);
        return index;
    };
    const faceCount = geometry.indices.length / 3;
    for (let f = 0; f < faceCount; f++) {
        const i0 = weldedIndexOf(geometry.indices[f * 3]);
        const i1 = weldedIndexOf(geometry.indices[f * 3 + 1]);
        const i2 = weldedIndexOf(geometry.indices[f * 3 + 2]);
        if (i0 === i1 || i1 === i2 || i2 === i0)
            continue;
        indices.push(i0, i1, i2);
    }
    return { vertices, indices };
}
/** Default snapping distance for stitching (m); LandXML rounds to ~1 um. */
export const DEFAULT_STITCH_TOLERANCE = 1e-3;
/** Cell size of the lookup grid used to find vertices lying on an edge (m). */
const STITCH_GRID_CELL = 1;
/** Distance from `p` to segment `a`-`b`, plus the projection parameter. */
function distanceToSegment(p, a, b) {
    const abx = b[0] - a[0];
    const aby = b[1] - a[1];
    const abz = b[2] - a[2];
    const lengthSquared = abx * abx + aby * aby + abz * abz;
    if (lengthSquared === 0)
        return { distance: Number.POSITIVE_INFINITY, t: 0 };
    const t = ((p[0] - a[0]) * abx + (p[1] - a[1]) * aby + (p[2] - a[2]) * abz) /
        lengthSquared;
    const dx = p[0] - (a[0] + abx * t);
    const dy = p[1] - (a[1] + aby * t);
    const dz = p[2] - (a[2] + abz * t);
    return { distance: Math.hypot(dx, dy, dz), t };
}
/** XY grid of vertex indices, so an edge only tests vertices near it. */
function buildVertexGrid(vertices, candidates) {
    const grid = new Map();
    for (const vertex of candidates) {
        const cx = Math.floor(vertices[vertex * 3] / STITCH_GRID_CELL);
        const cy = Math.floor(vertices[vertex * 3 + 1] / STITCH_GRID_CELL);
        const key = `${cx}_${cy}`;
        const cell = grid.get(key);
        if (cell)
            cell.push(vertex);
        else
            grid.set(key, [vertex]);
    }
    return grid;
}
/** Vertices in the cells the segment passes through, including neighbours. */
function candidatesAlongSegment(grid, ax, ay, bx, by) {
    const found = new Set();
    const length = Math.hypot(bx - ax, by - ay);
    const steps = Math.max(1, Math.ceil(length / STITCH_GRID_CELL));
    for (let step = 0; step <= steps; step++) {
        const t = step / steps;
        const cx = Math.floor((ax + (bx - ax) * t) / STITCH_GRID_CELL);
        const cy = Math.floor((ay + (by - ay) * t) / STITCH_GRID_CELL);
        for (let ox = -1; ox <= 1; ox++) {
            for (let oy = -1; oy <= 1; oy++) {
                for (const vertex of grid.get(`${cx + ox}_${cy + oy}`) ?? []) {
                    found.add(vertex);
                }
            }
        }
    }
    return found;
}
/** Max stitching passes; a split can expose further T-junctions on its new edges. */
const MAX_STITCH_PASSES = 8;
/**
 * Close T-junctions on the boundary: where a boundary vertex lies on another
 * boundary edge, split that edge at it and fan-split the incident triangle, so
 * the two sides become one shared edge.
 *
 * Imported design data arrives this way — a flat top authored as one quad next
 * to a slope whose top edge carries extra vertices. Geometrically there is no
 * gap, but the consumer walks outer edges and sees the overlap as two rings.
 * @param tolerance How far off the segment a vertex may sit and still count (m)
 * @returns New geometry; the input is not mutated
 */
export function stitchTJunctions(geometry, tolerance = DEFAULT_STITCH_TOLERANCE) {
    const vertices = [...geometry.vertices];
    const indices = [...geometry.indices];
    for (let pass = 0; pass < MAX_STITCH_PASSES; pass++) {
        const boundaryEdges = collectBoundaryEdges({ vertices, indices });
        const edgeOwner = new Map();
        const boundaryVertices = new Set();
        for (const edge of boundaryEdges) {
            edgeOwner.set(edgeKey(edge.a, edge.b), edge.face);
            boundaryVertices.add(edge.a);
            boundaryVertices.add(edge.b);
        }
        const grid = buildVertexGrid(vertices, [...boundaryVertices]);
        let stitched = 0;
        for (const edge of boundaryEdges) {
            const owner = edgeOwner.get(edgeKey(edge.a, edge.b));
            if (owner === undefined)
                continue;
            const ax = vertices[edge.a * 3];
            const ay = vertices[edge.a * 3 + 1];
            const az = vertices[edge.a * 3 + 2];
            const bx = vertices[edge.b * 3];
            const by = vertices[edge.b * 3 + 1];
            const bz = vertices[edge.b * 3 + 2];
            const hits = [];
            for (const vertex of candidatesAlongSegment(grid, ax, ay, bx, by)) {
                if (vertex === edge.a || vertex === edge.b)
                    continue;
                const px = vertices[vertex * 3];
                const py = vertices[vertex * 3 + 1];
                const pz = vertices[vertex * 3 + 2];
                // A vertex sitting on an endpoint is a weld miss, not a T-junction;
                // splitting there yields a sliver that overlaps the neighbouring face.
                if (Math.hypot(px - ax, py - ay, pz - az) <= tolerance ||
                    Math.hypot(px - bx, py - by, pz - bz) <= tolerance) {
                    continue;
                }
                const { distance, t } = distanceToSegment([px, py, pz], [ax, ay, az], [bx, by, bz]);
                if (distance <= tolerance && t > 0 && t < 1)
                    hits.push({ t, vertex });
            }
            if (hits.length === 0)
                continue;
            hits.sort((left, right) => left.t - right.t);
            const chain = [edge.a, ...hits.map((hit) => hit.vertex), edge.b];
            fanSplitFace(indices, owner, edge.a, edge.b, chain, edgeOwner);
            stitched += hits.length;
        }
        if (stitched === 0)
            break;
    }
    return { vertices, indices };
}
/**
 * Vertices whose incident boundary edges are not exactly 2, i.e. the mesh has no
 * simple outer ring there (holes touching, bow ties, disjoint fans).
 */
export function findNonSimpleBoundaryVertices(geometry) {
    const valence = new Map();
    for (const edge of collectBoundaryEdges(geometry)) {
        valence.set(edge.a, (valence.get(edge.a) ?? 0) + 1);
        valence.set(edge.b, (valence.get(edge.b) ?? 0) + 1);
    }
    const result = [];
    for (const [vertex, count] of valence) {
        if (count !== 2)
            result.push(vertex);
    }
    return result;
}
const MERGE_TOLERANCES = [1e-6, 1e-4, 1e-3, 1e-2];
/** How many vertices collapse when positions are snapped to `tolerance`. */
function countMergeableVertices(geometry, tolerance) {
    const buckets = new Set();
    const vertexCount = geometry.vertices.length / 3;
    for (let v = 0; v < vertexCount; v++) {
        const x = Math.round(geometry.vertices[v * 3] / tolerance);
        const y = Math.round(geometry.vertices[v * 3 + 1] / tolerance);
        const z = Math.round(geometry.vertices[v * 3 + 2] / tolerance);
        buckets.add(`${x} ${y} ${z}`);
    }
    return vertexCount - buckets.size;
}
const MAX_REPORTED_VERTICES = 8;
/**
 * Describe a geometry's boundary: counts plus the coordinates of vertices whose
 * boundary is not a simple ring. Meant for error messages and logging.
 */
export function describeBoundary(geometry) {
    const faceCount = geometry.indices.length / 3;
    const edgeUses = new Map();
    for (let f = 0; f < faceCount; f++) {
        const i0 = geometry.indices[f * 3];
        const i1 = geometry.indices[f * 3 + 1];
        const i2 = geometry.indices[f * 3 + 2];
        for (const key of [edgeKey(i0, i1), edgeKey(i1, i2), edgeKey(i2, i0)]) {
            edgeUses.set(key, (edgeUses.get(key) ?? 0) + 1);
        }
    }
    let overSharedEdgeCount = 0;
    for (const uses of edgeUses.values()) {
        if (uses > 2)
            overSharedEdgeCount++;
    }
    const boundaryEdges = collectBoundaryEdges(geometry);
    const valence = new Map();
    for (const edge of boundaryEdges) {
        valence.set(edge.a, (valence.get(edge.a) ?? 0) + 1);
        valence.set(edge.b, (valence.get(edge.b) ?? 0) + 1);
    }
    const offending = [];
    let nonSimpleBoundaryVertexCount = 0;
    for (const [vertex, count] of valence) {
        if (count === 2)
            continue;
        nonSimpleBoundaryVertexCount++;
        if (offending.length < MAX_REPORTED_VERTICES) {
            offending.push({
                x: geometry.vertices[vertex * 3],
                y: geometry.vertices[vertex * 3 + 1],
                z: geometry.vertices[vertex * 3 + 2],
                boundaryValence: count,
            });
        }
    }
    const mergeableAtTolerance = {};
    for (const tolerance of MERGE_TOLERANCES) {
        mergeableAtTolerance[String(tolerance)] = countMergeableVertices(geometry, tolerance);
    }
    return {
        vertexCount: geometry.vertices.length / 3,
        faceCount,
        boundaryEdgeCount: boundaryEdges.length,
        overSharedEdgeCount,
        nonSimpleBoundaryVertexCount,
        nonSimpleBoundaryVertices: offending,
        mergeableAtTolerance,
    };
}
/** Boundary edges (exactly one incident face), oriented by that face's winding. */
function collectBoundaryEdges(geometry) {
    const faceCount = geometry.indices.length / 3;
    const edges = new Map();
    for (let f = 0; f < faceCount; f++) {
        const i0 = geometry.indices[f * 3];
        const i1 = geometry.indices[f * 3 + 1];
        const i2 = geometry.indices[f * 3 + 2];
        for (const [a, b] of [
            [i0, i1],
            [i1, i2],
            [i2, i0],
        ]) {
            const key = edgeKey(a, b);
            const entry = edges.get(key);
            if (entry) {
                entry.count++;
            }
            else {
                edges.set(key, { count: 1, edge: { a, b, face: f } });
            }
        }
    }
    const result = [];
    for (const { count, edge } of edges.values()) {
        if (count === 1)
            result.push(edge);
    }
    return result;
}
/**
 * Vertex chain a -> ... -> b with intermediate points every `pitch` (XY length).
 * Returns [a, b] when no subdivision is needed.
 */
function subdivideEdge(vertices, a, b, pitch, addVertex) {
    const ax = vertices[a * 3];
    const ay = vertices[a * 3 + 1];
    const az = vertices[a * 3 + 2];
    const bx = vertices[b * 3];
    const by = vertices[b * 3 + 1];
    const bz = vertices[b * 3 + 2];
    const lengthXY = Math.hypot(bx - ax, by - ay);
    const segments = Number.isFinite(pitch) && pitch > 0 ? Math.ceil(lengthXY / pitch) : 1;
    if (segments <= 1)
        return [a, b];
    const chain = [a];
    for (let k = 1; k < segments; k++) {
        const t = k / segments;
        chain.push(addVertex(ax + (bx - ax) * t, ay + (by - ay) * t, az + (bz - az) * t));
    }
    chain.push(b);
    return chain;
}
/**
 * Replace face (a, b, c) with the fan (chain[k], chain[k+1], c) along the
 * subdivided edge a -> b. The first fan triangle reuses the face's slot so
 * other boundary edges of the same face stay resolvable through edgeOwner.
 */
function fanSplitFace(indices, face, a, b, chain, edgeOwner) {
    const f0 = indices[face * 3];
    const f1 = indices[face * 3 + 1];
    const f2 = indices[face * 3 + 2];
    const c = f0 !== a && f0 !== b ? f0 : f1 !== a && f1 !== b ? f1 : f2;
    indices[face * 3] = chain[0];
    indices[face * 3 + 1] = chain[1];
    indices[face * 3 + 2] = c;
    for (let k = 1; k < chain.length - 1; k++) {
        indices.push(chain[k], chain[k + 1], c);
    }
    // Edge (c, a) stays on `face`; edge (b, c) moved to the last fan triangle.
    edgeOwner.set(edgeKey(b, c), indices.length / 3 - 1);
}
//# sourceMappingURL=mesh-terrain-extension.js.map